import { Suspense } from "react";
import { useAtomValue } from "jotai";
import {
  chatbotAtomFamily_SUSPENDS,
  DefaultNodeData,
  interfaceProjectAtomFamily_SUSPENDS,
  interfacePageAtomFamily_SUSPENDS,
  tableAtomFamily_SUSPENDS,
} from "@zapier/canvas-state";
import styles from "./AssetName.module.css";

// Don't need to trim for interface since name won't include this (see interfaces/route for details)
function trimAssetName(assetName: string) {
  return assetName.replace(/ \((created by Canvas)\)$/i, "");
}

type SuspendedTableNameProps = {
  tableId: DefaultNodeData["tableId"];
};

function SuspendedTableName({ tableId }: SuspendedTableNameProps) {
  const table = useAtomValue(tableAtomFamily_SUSPENDS(tableId));

  if (table?.name) {
    return (
      <div className={styles.truncateText}>{trimAssetName(table.name)}</div>
    );
  }

  return null;
}

type SuspendedInterfaceProjectNameProps = {
  nodeId: string;
  interfaceProjectId: DefaultNodeData["projectId"];
};

function SuspendedInterfaceProjectName({
  nodeId,
  interfaceProjectId,
}: SuspendedInterfaceProjectNameProps) {
  const interfaceProject = useAtomValue(
    interfaceProjectAtomFamily_SUSPENDS({ nodeId, interfaceProjectId }),
  );

  if (interfaceProject?.name) {
    return <div className={styles.truncateText}>{interfaceProject.name}</div>;
  }

  return null;
}

type SuspendedInterfacePageNameProps = {
  nodeId: string;
  interfaceProjectId: DefaultNodeData["projectId"];
  interfacePageId: DefaultNodeData["pageId"];
};

function SuspendedInterfacePageName({
  nodeId,
  interfaceProjectId,
  interfacePageId,
}: SuspendedInterfacePageNameProps) {
  const interfacePage = useAtomValue(
    interfacePageAtomFamily_SUSPENDS({
      nodeId,
      interfaceProjectId,
      interfacePageId,
    }),
  );

  if (interfacePage?.name) {
    return <div className={styles.truncateText}>{interfacePage.name}</div>;
  }

  return null;
}

type SuspendedChatbotNameProps = {
  chatbotId: DefaultNodeData["chatbotId"];
};

function SuspendedChatbotName({ chatbotId }: SuspendedChatbotNameProps) {
  const chatbot = useAtomValue(chatbotAtomFamily_SUSPENDS(chatbotId));

  if (chatbot?.name) {
    return (
      <div className={styles.truncateText}>{trimAssetName(chatbot.name)}</div>
    );
  }

  return null;
}

type AssetNameProps = {
  nodeId: string;
  tableId: DefaultNodeData["tableId"];
  interfaceProjectId: DefaultNodeData["projectId"];
  interfacePageId: DefaultNodeData["pageId"];
  chatbotId: DefaultNodeData["chatbotId"];
};

export function AssetName({
  nodeId,
  tableId,
  interfaceProjectId,
  interfacePageId,
  chatbotId,
}: AssetNameProps) {
  function getComponent() {
    if (tableId) {
      return <SuspendedTableName tableId={tableId} />;
    }

    if (interfacePageId) {
      return (
        <SuspendedInterfacePageName
          nodeId={nodeId}
          interfaceProjectId={interfaceProjectId}
          interfacePageId={interfacePageId}
        />
      );
    }

    if (interfaceProjectId) {
      return (
        <SuspendedInterfaceProjectName
          nodeId={nodeId}
          interfaceProjectId={interfaceProjectId}
        />
      );
    }

    if (chatbotId) {
      return <SuspendedChatbotName chatbotId={chatbotId} />;
    }
  }

  return <Suspense fallback={null}>{getComponent()}</Suspense>;
}
